import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { dispatchTicketsBulkUpdated } from "../../utils/ticketEvents";

const getReadableTextColor = color => {
  const hex = (color || "#ffffff").replace("#", "");
  const normalized =
    hex.length === 3
      ? hex.split("").map(char => `${char}${char}`).join("")
      : hex.padEnd(6, "f").slice(0, 6);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

  return luminance > 150 ? "#243040" : "#ffffff";
};

const getAssignmentMode = mode =>
  mode === "normal" ? "normal" : "multiagent";

const normalizeStoredIds = values =>
  Array.isArray(values) ? values.map(Number).filter(Boolean) : [];

const getStoredFilterSelections = storageKey => {
  try {
    const parsedValue = JSON.parse(localStorage.getItem(storageKey) || "{}");
    return {
      queueIds: normalizeStoredIds(parsedValue.queueIds),
      connectionIds: normalizeStoredIds(parsedValue.connectionIds),
      userIds: normalizeStoredIds(parsedValue.userIds),
    };
  } catch (err) {
    return {
      queueIds: [],
      connectionIds: [],
      userIds: [],
    };
  }
};

const filterExistingIds = (selectedIds, options) => {
  if (!Array.isArray(options) || !options.length) return selectedIds;

  const existingIds = new Set(options.map(option => Number(option.id)));
  const filteredIds = selectedIds.filter(id => existingIds.has(Number(id)));
  return filteredIds.length === selectedIds.length ? selectedIds : filteredIds;
};

const normalizeConfigIds = values =>
  Array.isArray(values) ? values.map(Number).filter(Boolean) : [];

const chatTypeFilterValues = ["all", "groups", "contacts", "unread", "ignored"];
const NO_BULK_QUEUE = "__no_bulk_queue__";
const NO_BULK_USER = "__no_bulk_user__";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    border: "none",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
  },
  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tab: {
    minWidth: 120,
    width: 120,
  },
  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
  serachInputWrapper: {
    flex: 1,
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary, 
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    right: "-10px",
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
  labelsFilter: {
    minWidth: 130,
    width: "100%",
  },
  labelsFilterValue: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    minHeight: 24,
  },
  labelChip: {
    height: 22,
    maxWidth: "100%",
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  filtersRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(120px, 1fr))",
    gap: theme.spacing(1),
    width: "100%",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  chatTypeFilters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1.25),
    flexWrap: "wrap",
    backgroundColor: theme.palette.background.paper,
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  autoAssignmentButton: {
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
    },
  },
  autoAssignmentSummary: {
    marginBottom: theme.spacing(1),
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },
  chatTypeChip: {
    height: 32,
    borderRadius: 16,
    fontWeight: 500,
  },
}));

const TicketsManager = ({
  labels = [],
  selectedLabelIds = [],
  setSelectedLabelIds = () => {},
  selectedFunnelId = "",
  selectedFunnelStageId = "",
  refreshToken,
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const canUseAllTicketFilters = ["admin", "superadmin", "owner"].includes(
    String(user?.profile || "").toLowerCase()
  );
  const showAllTicketsStorageKey = `showAllTickets:${user?.id || "default"}`;
  const chatTypeFilterStorageKey = `chatTypeFilter:${user?.id || "default"}`;
  const ticketFilterSelectionsStorageKey = `ticketFilterSelections:${
    user?.id || "default"
  }`;
  const assignmentModeStorageKey = `ticketAssignmentMode:${
    user?.companyId || user?.company?.id || "default"
  }`;
  const userQueueIds = user.queues.map((q) => q.id);
  const storedFilterSelections = getStoredFilterSelections(
    ticketFilterSelectionsStorageKey
  );
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("open");
  const [assignmentMode, setAssignmentMode] = useState(() => {
    const storedMode = localStorage.getItem(assignmentModeStorageKey);
    return storedMode ? getAssignmentMode(storedMode) : null;
  });
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(() => {
    const storedValue = localStorage.getItem(showAllTicketsStorageKey);
    if (storedValue !== null) return storedValue === "true";
    return user?.profile?.toUpperCase() === "ADMIN";
  });
  const searchInputRef = useRef();
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    storedFilterSelections.queueIds.length
      ? storedFilterSelections.queueIds
      : userQueueIds || []
  );
  const [queues, setQueues] = useState([]);
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState(
    storedFilterSelections.connectionIds
  );
  const [selectedUserIds, setSelectedUserIds] = useState(
    storedFilterSelections.userIds
  );
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkLabelIds, setBulkLabelIds] = useState([]);
  const [bulkQueueId, setBulkQueueId] = useState("");
  const [bulkUserId, setBulkUserId] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkRefreshToken, setBulkRefreshToken] = useState(0);
  const [chatTypeFilter, setChatTypeFilter] = useState(() => {
    const storedValue = localStorage.getItem(chatTypeFilterStorageKey);
    return chatTypeFilterValues.includes(storedValue) ? storedValue : "all";
  });
  const [autoAssignmentModalOpen, setAutoAssignmentModalOpen] = useState(false);
  const [autoAssignmentSaving, setAutoAssignmentSaving] = useState(false);
  const [autoAssignmentLoading, setAutoAssignmentLoading] = useState(false);
  const [autoAssignmentConfig, setAutoAssignmentConfig] = useState({
    enabled: false,
    queueIds: [],
    userIds: [],
  });
  const queueFilterOptions = canUseAllTicketFilters ? queues : user?.queues || [];
  const assignmentModeReady = Boolean(assignmentMode);
  const isNormalMode = assignmentMode === "normal";
  const showMultiagentControls = assignmentModeReady && !isNormalMode;
  const canManageAutoAssignment = canUseAllTicketFilters;
  const secondaryTabValue = isNormalMode ? "closed" : "pending";
  const forcedShowAll = canUseAllTicketFilters ? true : undefined;
  const chatTypeFilterOptions = [
    { value: "all", label: i18n.t("tickets.chatFilters.all") },
    { value: "groups", label: i18n.t("tickets.chatFilters.groups") },
    { value: "contacts", label: i18n.t("tickets.chatFilters.contacts") },
    { value: "unread", label: i18n.t("tickets.chatFilters.unread") },
    { value: "ignored", label: "Ignorados" },
  ];
  const bulkActions = [
    { value: "addLabels", label: "Aplicar etiquetas" },
    { value: "removeLabels", label: "Quitar etiquetas" },
    { value: "transfer", label: "Transferir" },
    { value: "resolve", label: "Resolver chats" },
    { value: "return", label: "Devolver chats" },
    { value: "ignore", label: "Ignorar chats" },
    { value: "unignore", label: "Quitar de ignorados" },
  ];

  const normalizeAutoAssignmentConfig = data => ({
    enabled: Boolean(data?.enabled),
    queueIds: normalizeConfigIds(data?.queueIds),
    userIds: normalizeConfigIds(data?.userIds),
  });

  useEffect(() => {
    localStorage.setItem(showAllTicketsStorageKey, String(showAllTickets));
  }, [showAllTickets, showAllTicketsStorageKey]);

  useEffect(() => {
    localStorage.setItem(chatTypeFilterStorageKey, chatTypeFilter);
  }, [chatTypeFilter, chatTypeFilterStorageKey]);

  useEffect(() => {
    localStorage.setItem(
      ticketFilterSelectionsStorageKey,
      JSON.stringify({
        queueIds: selectedQueueIds,
        connectionIds: selectedConnectionIds,
        userIds: selectedUserIds,
      })
    );
  }, [
    ticketFilterSelectionsStorageKey,
    selectedQueueIds,
    selectedConnectionIds,
    selectedUserIds,
  ]);

  useEffect(() => {
    setSelectedQueueIds(prevState =>
      filterExistingIds(prevState, queueFilterOptions)
    );
  }, [queueFilterOptions]);

  useEffect(() => {
    setSelectedConnectionIds(prevState =>
      filterExistingIds(prevState, connections)
    );
  }, [connections]);

  useEffect(() => {
    setSelectedUserIds(prevState => filterExistingIds(prevState, users));
  }, [users]);

  useEffect(() => {
    if (tab === "search" || isNormalMode) {
      searchInputRef.current?.focus();
      if (!isNormalMode) {
        setSearchParam("");
      }
    }
  }, [tab, isNormalMode]);

  useEffect(() => {
    const fetchAssignmentMode = async () => {
      try {
        const { data } = await api.get("/settings/ticket-assignment-mode");
        const mode = getAssignmentMode(data.mode);
        localStorage.setItem(assignmentModeStorageKey, mode);
        setAssignmentMode(mode);
      } catch (err) {
        toastError(err);
      }
    };

    const handleAssignmentModeChanged = event => {
      const mode = getAssignmentMode(event.detail?.mode);
      localStorage.setItem(assignmentModeStorageKey, mode);
      setAssignmentMode(mode);
      if (mode === "normal") {
        setTab("open");
        setTabOpen("open");
      } else {
        setTabOpen(prevMode => (prevMode === "closed" ? "open" : prevMode));
      }
    };

    fetchAssignmentMode();
    window.addEventListener(
      "ticketAssignmentModeChanged",
      handleAssignmentModeChanged
    );

    return () => {
      window.removeEventListener(
        "ticketAssignmentModeChanged",
        handleAssignmentModeChanged
      );
    };
  }, [assignmentModeStorageKey]);

  useEffect(() => {
    (async () => {
      try {
        const [
          { data: connectionData },
          { data: userData },
          { data: queueData },
        ] = await Promise.all([
          api.get("/connections/"),
          api.get("/users/", { params: { searchParam: "", pageNumber: 1 } }),
          canUseAllTicketFilters ? api.get("/queue") : Promise.resolve({ data: [] }),
        ]);
        setConnections(connectionData || []);
        setUsers(userData.users || []);
        setQueues(queueData || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [canUseAllTicketFilters]);

  useEffect(() => {
    if (!isNormalMode) return;
    setAutoAssignmentModalOpen(false);
  }, [isNormalMode]);

  useEffect(() => {
    if (!autoAssignmentModalOpen) return;

    const fetchAutoAssignmentConfig = async () => {
      setAutoAssignmentLoading(true);
      try {
        const { data } = await api.get("/settings/multiagent-auto-assignment");
        setAutoAssignmentConfig(normalizeAutoAssignmentConfig(data));
      } catch (err) {
        toastError(err);
      } finally {
        setAutoAssignmentLoading(false);
      }
    };

    fetchAutoAssignmentConfig();
  }, [autoAssignmentModalOpen]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      if (!isNormalMode) {
        setTab("open");
      }
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkAction("");
    setBulkLabelIds([]);
    setBulkQueueId("");
    setBulkUserId("");
  };

  const handleBulkApply = async () => {
    if (!bulkAction || !selectedTicketIds.length) return;

    setBulkSaving(true);
    try {
      const { data } = await api.put("/tickets/bulk", {
        action: bulkAction,
        ticketIds: selectedTicketIds,
        labelIds: bulkLabelIds,
        queueId: bulkQueueId || null,
        userId: bulkUserId || null,
      });
      dispatchTicketsBulkUpdated(data.tickets || []);
      toast.success("Chats actualizados");
      setSelectedTicketIds([]);
      setBulkRefreshToken(prevState => prevState + 1);
      closeBulkModal();
    } catch (err) {
      toastError(err);
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSaveAutoAssignment = async () => {
    setAutoAssignmentSaving(true);
    try {
      const { data } = await api.put("/settings/multiagent-auto-assignment", {
        enabled: autoAssignmentConfig.enabled,
        queueIds: autoAssignmentConfig.queueIds,
        userIds: autoAssignmentConfig.userIds,
      });
      setAutoAssignmentConfig(normalizeAutoAssignmentConfig(data));
      toast.success("Configuracion de autoasignacion guardada");
      setAutoAssignmentModalOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setAutoAssignmentSaving(false);
    }
  };

  const bulkActionRequiresLabels = ["addLabels", "removeLabels"].includes(bulkAction);
  const bulkActionRequiresTransfer = bulkAction === "transfer";
  const bulkApplyDisabled =
    bulkSaving ||
    !bulkAction ||
    !selectedTicketIds.length ||
    (bulkActionRequiresLabels && !bulkLabelIds.length) ||
    (bulkActionRequiresTransfer && !bulkQueueId && !bulkUserId);
  const combinedRefreshToken = `${refreshToken || 0}-${bulkRefreshToken}`;

  const selectedAutoAssignmentQueues = queueFilterOptions.filter(queue =>
    autoAssignmentConfig.queueIds.some(queueId => Number(queueId) === Number(queue.id))
  );
  const selectedAutoAssignmentUsers = users.filter(item =>
    autoAssignmentConfig.userIds.some(userId => Number(userId) === Number(item.id))
  );
  const autoAssignmentSaveDisabled =
    autoAssignmentSaving ||
    autoAssignmentLoading ||
    (autoAssignmentConfig.enabled &&
      !autoAssignmentConfig.queueIds.length &&
      !autoAssignmentConfig.userIds.length);

  const renderChatTypeFilters = () => (
    <div className={classes.chatTypeFilters} role="tablist" aria-label="chat-list-filters">
      {chatTypeFilterOptions.map(option => (
        <Chip
          key={option.value}
          label={option.label}
          clickable
          role="tab"
          aria-selected={chatTypeFilter === option.value}
          color={chatTypeFilter === option.value ? "primary" : "default"}
          className={classes.chatTypeChip}
          onClick={() => setChatTypeFilter(option.value)}
        />
      ))}
      {showMultiagentControls && canManageAutoAssignment && (
        <Tooltip title="Configurar autoasignacion">
          <IconButton
            size="small"
            className={classes.autoAssignmentButton}
            onClick={() => setAutoAssignmentModalOpen(true)}
            aria-label="Configurar autoasignacion"
          >
            <SettingsOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  useEffect(() => {
    setSelectedTicketIds([]);
  }, [
    tab,
    tabOpen,
    chatTypeFilter,
    selectedQueueIds,
    selectedLabelIds,
    selectedConnectionIds,
    selectedUserIds,
    selectedFunnelId,
    selectedFunnelStageId,
    searchParam,
  ]);

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      <Dialog open={bulkModalOpen} onClose={closeBulkModal} fullWidth maxWidth="sm">
        <DialogTitle>Gestionar chats seleccionados</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            {selectedTicketIds.length} chat(s) seleccionados
          </Typography>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Acción</InputLabel>
            <Select
              value={bulkAction}
              onChange={event => setBulkAction(event.target.value)}
              label="Acción"
            >
              {bulkActions.map(action => (
                <MenuItem key={action.value} value={action.value}>
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {bulkActionRequiresLabels && (
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Etiquetas</InputLabel>
              <Select
                multiple
                value={bulkLabelIds}
                onChange={event => setBulkLabelIds(event.target.value)}
                label="Etiquetas"
                renderValue={selected =>
                  labels
                    .filter(label => selected.some(labelId => String(labelId) === String(label.id)))
                    .map(label => label.name)
                    .join(", ")
                }
              >
                {labels.map(label => (
                  <MenuItem key={label.id} value={label.id}>
                    <Checkbox checked={bulkLabelIds.some(labelId => String(labelId) === String(label.id))} />
                    <ListItemText primary={label.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {bulkActionRequiresTransfer && (
            <>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  value={bulkQueueId || NO_BULK_QUEUE}
                  onChange={event =>
                    setBulkQueueId(
                      event.target.value === NO_BULK_QUEUE ? "" : event.target.value
                    )
                  }
                  label="Departamento"
                >
                  <MenuItem value={NO_BULK_QUEUE}>Sin departamento</MenuItem>
                  {queueFilterOptions.map(queue => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Usuario</InputLabel>
                <Select
                  value={bulkUserId || NO_BULK_USER}
                  onChange={event =>
                    setBulkUserId(
                      event.target.value === NO_BULK_USER ? "" : event.target.value
                    )
                  }
                  label="Usuario"
                >
                  <MenuItem value={NO_BULK_USER}>Sin usuario</MenuItem>
                  {users.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkModal} variant="contained">
            Cancelar
          </Button>
          <Button
            onClick={handleBulkApply}
            color="primary"
            variant="contained"
            disabled={bulkApplyDisabled}
          >
            {bulkSaving ? "Aplicando..." : "Aplicar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={autoAssignmentModalOpen}
        onClose={() => setAutoAssignmentModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Autoasignacion multiagente</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.autoAssignmentSummary}
          >
            Cuando esta activa, el sistema reparte los chats nuevos uno por uno
            entre los departamentos y usuarios seleccionados, empezando desde el
            momento en que se activa. Si la desactivas, los chats seguiran
            entrando sin autoasignarse.
          </Typography>
          <FormControlLabel
            label="Activar autoasignacion"
            control={
              <Switch
                color="primary"
                checked={autoAssignmentConfig.enabled}
                onChange={event =>
                  setAutoAssignmentConfig(prevState => ({
                    ...prevState,
                    enabled: event.target.checked,
                  }))
                }
              />
            }
          />
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Departamentos</InputLabel>
            <Select
              multiple
              value={autoAssignmentConfig.queueIds}
              onChange={event =>
                setAutoAssignmentConfig(prevState => ({
                  ...prevState,
                  queueIds: normalizeConfigIds(event.target.value),
                }))
              }
              label="Departamentos"
              disabled={!autoAssignmentConfig.enabled || autoAssignmentLoading}
              renderValue={() =>
                selectedAutoAssignmentQueues.length
                  ? selectedAutoAssignmentQueues.map(queue => queue.name).join(", ")
                  : "Sin departamentos"
              }
            >
              {queueFilterOptions.map(queue => (
                <MenuItem key={queue.id} value={queue.id}>
                  <Checkbox
                    checked={autoAssignmentConfig.queueIds.some(
                      queueId => Number(queueId) === Number(queue.id)
                    )}
                  />
                  <ListItemText primary={queue.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Usuarios</InputLabel>
            <Select
              multiple
              value={autoAssignmentConfig.userIds}
              onChange={event =>
                setAutoAssignmentConfig(prevState => ({
                  ...prevState,
                  userIds: normalizeConfigIds(event.target.value),
                }))
              }
              label="Usuarios"
              disabled={!autoAssignmentConfig.enabled || autoAssignmentLoading}
              renderValue={() =>
                selectedAutoAssignmentUsers.length
                  ? selectedAutoAssignmentUsers.map(item => item.name).join(", ")
                  : "Sin usuarios"
              }
            >
              {users.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox
                    checked={autoAssignmentConfig.userIds.some(
                      userId => Number(userId) === Number(item.id)
                    )}
                  />
                  <ListItemText primary={item.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {autoAssignmentConfig.enabled &&
            !autoAssignmentConfig.queueIds.length &&
            !autoAssignmentConfig.userIds.length && (
              <Typography variant="caption" color="error">
                Selecciona al menos un departamento o usuario para activar el reparto.
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAutoAssignmentModalOpen(false)}
            variant="contained"
            disabled={autoAssignmentSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveAutoAssignment}
            color="primary"
            variant="contained"
            disabled={autoAssignmentSaveDisabled}
          >
            {autoAssignmentSaving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
      {showMultiagentControls && (
        <Paper elevation={0} square className={classes.tabsHeader}>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="icon label tabs example"
          >
            <Tab
              value={"open"}
              icon={<MoveToInboxIcon />}
              label={i18n.t("tickets.tabs.open.title")}
              classes={{ root: classes.tab }}
            />
            <Tab
              value={"closed"}
              icon={<CheckBoxIcon />}
              label={i18n.t("tickets.tabs.closed.title")}
              classes={{ root: classes.tab }}
            />
            <Tab
              value={"search"}
              icon={<SearchIcon />}
              label={i18n.t("tickets.tabs.search.title")}
              classes={{ root: classes.tab }}
            />
          </Tabs>
        </Paper>
      )}
      {assignmentModeReady && (
        <Paper square elevation={0} className={classes.ticketOptionsBox}>
        {isNormalMode || tab === "search" ? (
          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        ) : (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setNewTicketModalOpen(true)}
            >
              {i18n.t("ticketsManager.buttons.newTicket")}
            </Button>
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <FormControlLabel
                  label={i18n.t("tickets.buttons.showAll")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={showAllTickets}
                      onChange={() =>
                        setShowAllTickets((prevState) => !prevState)
                      }
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              )}
            />
          </>
        )}
        {showMultiagentControls && (
          <>
            <TicketsQueueSelect
              style={{ marginLeft: 6 }}
              selectedQueueIds={selectedQueueIds}
              userQueues={queueFilterOptions}
              onChange={(values) => setSelectedQueueIds(values)}
            />
            <div className={classes.filtersRow}>
              <FormControl
                variant="outlined"
                margin="dense"
                size="small"
                className={classes.labelsFilter}
              >
                <InputLabel>Conexiones</InputLabel>
                <Select
                  multiple
                  value={selectedConnectionIds}
                  onChange={event => setSelectedConnectionIds(event.target.value)}
                  label="Conexiones"
                  renderValue={selected =>
                    selected.length
                      ? connections
                          .filter(whatsapp => selected.includes(whatsapp.id))
                          .map(whatsapp => whatsapp.name || whatsapp.number)
                          .join(", ")
                      : "Conexiones"
                  }
                >
                  {connections.map(whatsapp => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      <Checkbox checked={selectedConnectionIds.indexOf(whatsapp.id) > -1} />
                      <ListItemText primary={whatsapp.name || whatsapp.number || `Conexion ${whatsapp.id}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                margin="dense"
                size="small"
                className={classes.labelsFilter}
              >
                <InputLabel>Usuarios</InputLabel>
                <Select
                  multiple
                  value={selectedUserIds}
                  onChange={event => setSelectedUserIds(event.target.value)}
                  label="Usuarios"
                  renderValue={selected =>
                    selected.length
                      ? users
                          .filter(item => selected.includes(item.id))
                          .map(item => item.name)
                          .join(", ")
                      : "Usuarios"
                  }
                >
                  {users.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      <Checkbox checked={selectedUserIds.indexOf(item.id) > -1} />
                      <ListItemText primary={item.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                margin="dense"
                size="small"
                className={classes.labelsFilter}
              >
                <InputLabel>{i18n.t("ticketLabels.filterLabel")}</InputLabel>
                <Select
                  multiple
                  value={selectedLabelIds}
                  onChange={event => setSelectedLabelIds(event.target.value)}
                  label={i18n.t("ticketLabels.filterLabel")}
                  renderValue={selected => (
                    <div className={classes.labelsFilterValue}>
                      {selected.length ? (
                        labels
                          .filter(label =>
                            selected.some(labelId => String(labelId) === String(label.id))
                          )
                          .map(label => (
                            <Chip
                              key={label.id}
                              label={label.name}
                              className={classes.labelChip}
                              style={{
                                backgroundColor: label.color,
                                color: getReadableTextColor(label.color),
                              }}
                            />
                          ))
                      ) : (
                        <span>&ZeroWidthSpace;</span>
                      )}
                    </div>
                  )}
                >
                  {labels.map(label => (
                    <MenuItem key={label.id} value={label.id}>
                      <Checkbox
                        style={{ color: label.color }}
                        checked={selectedLabelIds.some(labelId => String(labelId) === String(label.id))}
                      />
                      <ListItemText primary={label.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </>
        )}
        </Paper>
      )}
      {selectedTicketIds.length > 0 && (
        <div className={classes.bulkBar}>
          <Typography variant="body2">
            {selectedTicketIds.length} seleccionado(s)
          </Typography>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setBulkModalOpen(true)}
          >
            Gestionar
          </Button>
        </div>
      )}
      <TabPanel value={isNormalMode || !assignmentModeReady ? "open" : tab} name="open" className={classes.ticketsWrapper}>
        {assignmentModeReady && (
          <Tabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  badgeContent={openCount}
                  color="primary"
                >
                  {i18n.t("ticketsList.assignedHeader")}
                </Badge>
              }
              value={"open"}
            />
            <Tab
              label={
                isNormalMode ? (
                  i18n.t("tickets.tabs.closed.title")
                ) : (
                  <Badge
                    className={classes.badge}
                    badgeContent={pendingCount}
                    color="secondary"
                  >
                    {i18n.t("ticketsList.pendingHeader")}
                  </Badge>
                )
              }
              value={secondaryTabValue}
            />
          </Tabs>
        )}
        {assignmentModeReady && (
          renderChatTypeFilters()
        )}
        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            searchParam={isNormalMode ? searchParam : undefined}
            showAll={canUseAllTicketFilters ? showAllTickets : undefined}
            selectedQueueIds={selectedQueueIds}
            selectedLabelIds={selectedLabelIds}
            selectedConnectionIds={selectedConnectionIds}
            selectedUserIds={selectedUserIds}
            chatTypeFilter={chatTypeFilter}
            selectedFunnelId={selectedFunnelId}
            selectedFunnelStageId={selectedFunnelStageId}
            refreshToken={combinedRefreshToken}
            selectedTicketIds={selectedTicketIds}
            selectionMode={selectedTicketIds.length > 0}
            onSelectionChange={setSelectedTicketIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsList
            status={secondaryTabValue}
            searchParam={isNormalMode ? searchParam : undefined}
            showAll={isNormalMode ? forcedShowAll : undefined}
            selectedQueueIds={selectedQueueIds}
            selectedLabelIds={selectedLabelIds}
            selectedConnectionIds={selectedConnectionIds}
            selectedUserIds={selectedUserIds}
            chatTypeFilter={chatTypeFilter}
            selectedFunnelId={selectedFunnelId}
            selectedFunnelStageId={selectedFunnelStageId}
            refreshToken={combinedRefreshToken}
            selectedTicketIds={selectedTicketIds}
            selectionMode={selectedTicketIds.length > 0}
            onSelectionChange={setSelectedTicketIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle(secondaryTabValue)}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        {assignmentModeReady && !isNormalMode && (
          renderChatTypeFilters()
        )}
        <TicketsList
          status="closed"
          showAll={forcedShowAll}
          selectedQueueIds={selectedQueueIds}
          selectedLabelIds={selectedLabelIds}
          selectedConnectionIds={selectedConnectionIds}
          selectedUserIds={selectedUserIds}
          chatTypeFilter={chatTypeFilter}
          selectedFunnelId={selectedFunnelId}
          selectedFunnelStageId={selectedFunnelStageId}
          refreshToken={combinedRefreshToken}
          selectedTicketIds={selectedTicketIds}
          selectionMode={selectedTicketIds.length > 0}
          onSelectionChange={setSelectedTicketIds}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={forcedShowAll}
          selectedQueueIds={selectedQueueIds}
          selectedLabelIds={selectedLabelIds}
          selectedConnectionIds={selectedConnectionIds}
          selectedUserIds={selectedUserIds}
          chatTypeFilter={chatTypeFilter}
          selectedFunnelId={selectedFunnelId}
          selectedFunnelStageId={selectedFunnelStageId}
          refreshToken={combinedRefreshToken}
          selectedTicketIds={selectedTicketIds}
          selectionMode={selectedTicketIds.length > 0}
          onSelectionChange={setSelectedTicketIds}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;

